import $ from 'jquery';
import { nodeConfig } from '@/mixins';
import { getVideoAPIByType } from '@/modules/api/video';
import { appendScriptToBody } from '@/helpers/utils';
import { afterTransition } from '@/helpers/utils';

const NAMESPACE = 'background-video';
const DEFAULT_SELECTOR = '[data-js-background-video]';
const DEFAULT_SETTINGS = {
	//
};

class BackgroundVideo {
	constructor(node, config) {
		this.$node = $(node);
		this.node = node;
		this.config = config;

		this.$iframeTarget = this.$node.find('[data-js-background-video-iframe-target]').first();
		this.$playBtn = this.$node.find('[data-js-background-video-control="play"]');
		this.$pauseBtn = this.$node.find('[data-js-background-video-control="pause"]');

		this.api = null;
	}

	get $iframe() {
		return this.$node.find('iframe').first();
	}

	setApi(api) {
		this.api = api;
	}

	toggleBtnState(isPlaying = true) {
		this.$playBtn.toggleClass('background-video__control--pressed', isPlaying);
		this.$pauseBtn.toggleClass('background-video__control--pressed', !isPlaying);
	}

	muteAndPlay() {
		this.api.setVolumeByPercentage(0);

		this.play(false);
	}

	play(shouldFocus = true) {
		this.api.play();

		this.toggleBtnState(true);

		if (shouldFocus) {
			afterTransition(this.$pauseBtn[0], 'opacity', () => this.$pauseBtn[0].focus());
		}
	}

	pause(shouldFocus = true) {
		this.api.pause();

		this.toggleBtnState(false);

		if (shouldFocus) {
			afterTransition(this.$playBtn[0], 'opacity', () => this.$playBtn[0].focus());
		}
	}

	setVolumeByPercentage(num) {
		this.api.setVolumeByPercentage(num);
	}

	bindUI() {
		this.$playBtn.on('click', this.play.bind(this));
		this.$pauseBtn.on('click', this.pause.bind(this));
	}

	onPlayerReady() {
		this.$node.toggleClass('background-video--loaded', true);

		this.bindUI();
	}

	onScriptLoaded() {
		let config = Object.assign({}, this.config, {
			iframe: this.$iframeTarget[0],
			display: 'background'
		});

		return this.api.loadPlayer(config).then(this.onPlayerReady.bind(this));
	}

	onIntersecting() {
		let prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (this.config.autoplay && !prefersReducedMotion) {
			return appendScriptToBody(this.api.scriptUrl)
				.then(this.onScriptLoaded.bind(this))
				.then(this.muteAndPlay.bind(this));
		}

		return appendScriptToBody(this.api.scriptUrl).then(this.onScriptLoaded.bind(this));
	}

	setup() {
		this.toggleBtnState(false);

		let observer = new window.IntersectionObserver(
			(entries) => {
				let entry = entries[0];

				if (!entry.isIntersecting) return;

				this.onIntersecting();

				observer.unobserve(entry.target);
			},
			{
				rootMargin: '200px'
			}
		);

		observer.observe(this.node);
	}

	init() {
		if (!this.api) throw Error('An API must be provided');

		this.setup();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	let listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		let config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);

		if (!config.id || !config.type) return;

		let apiCtr = getVideoAPIByType(config.type);

		if (!apiCtr) {
			node.style.display = 'none';

			return;
		}

		let module = new BackgroundVideo(node, config);

		module.setApi(new apiCtr());

		module.init();

		return module;
	});
}
